import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyTenant } from '../hooks/useMyTenant';
import { apiFetch } from '../lib/api';

// Sprint ACAD ACT.A8 (#1709) — academic tenant admin onboarding wizard
// (one-time, per the AC's 7 steps): institution name → semester dates →
// faculty supervisor invite → API key entry → team names configuration →
// batch student invite → confirmation.
//
// Q2 adjudication (Sprint-ACT.A8-recon.md): POST /api/academic/tenants (the
// actual tenant-CREATION call collecting institution name/semester dates/
// faculty email/API key) is platform-admin-only and stays that way — AVR
// remains the provisioning actor for that step, done through his own
// platform-admin session, NOT through this wizard. By the time a
// TENANT_ADMIN reaches this page, that data already exists — so steps 1-4
// here are a READ-ONLY recap (via GET /api/academic/tenants/:id/info), not
// editable inputs re-triggering tenant creation.
//
// Step 5 (team names) is real, TENANT_ADMIN-gated CRUD ACT.A8 adds (Q1).
// R1/R2 follow-up fix: the mount effect now ALSO checks for an
// already-existing cohort (GET /api/academic/tenants/:id/cohorts) before ever
// offering to create one — restarting the wizard, or a browser reload
// mid-step-4, used to silently create a duplicate cohort since
// `createdCohortId` was pure client-side React state with no server check.
//
// Step 6 (batch student invite): ACT.A8 follow-up fix — POST
// /api/academic/tenants/:id/invite-batch's gate was widened
// (routes/academic-tenants.ts's requireInviteBatchAccess) to accept a
// TENANT_ADMIN/ADMIN scoped to their OWN tenant, in addition to the
// existing platform-admin path. This step now genuinely succeeds for the
// TENANT_ADMIN viewing this wizard — no more graceful-403 fallback.

interface RecapInfo {
  institutionName:        string;
  semesterStart:          string;
  semesterEnd:            string;
  facultySupervisorEmail: string;
  hasApiKey:              boolean;
}

const cardStyle: React.CSSProperties = {
  width: '100%', maxWidth: 520, background: 'var(--surface)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-lg)', padding: 40,
};

const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', minHeight: 44, background: 'var(--surface-alt)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 14px',
  fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', outline: 'none', boxSizing: 'border-box',
};

const buttonStyle: React.CSSProperties = {
  height: 44, padding: '0 24px', background: 'var(--primary)', color: 'white',
  fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600,
  borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
};

const STEP_COUNT = 7;

export default function AcademicAdminOnboardingPage() {
  const navigate = useNavigate();
  const { myTenant } = useMyTenant();
  const tenantId = myTenant?.tenantId;

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [recap, setRecap] = useState<RecapInfo | null>(null);

  const [cohortName, setCohortName] = useState('');
  const [cohortSemester, setCohortSemester] = useState('');
  const [teamNamesRaw, setTeamNamesRaw] = useState('');
  const [createdCohortId, setCreatedCohortId] = useState<string | null>(null);
  const [createdTeams, setCreatedTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [teamError, setTeamError] = useState('');

  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteTeamId, setInviteTeamId] = useState('');
  const [inviteResult, setInviteResult] = useState<{ sent: number; failed: number } | null>(null);
  const [inviteError, setInviteError] = useState('');

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    Promise.all([
      apiFetch<RecapInfo>(`/api/academic/tenants/${tenantId}/info`),
      apiFetch<{ currentStep: number }>('/api/onboarding/TENANT_ADMIN'),
      // R1/R2 follow-up fix — check for an ALREADY-EXISTING cohort on every
      // mount (restart, browser reload mid-flow, or a normal first visit),
      // not just within a single component lifetime. Without this, step 4
      // re-offered "Create cohort & teams" from scratch on any remount,
      // silently creating a duplicate cohort — no server-side idempotency
      // check existed before this fix. Q8's "pre-filled/editable" restart
      // semantics require knowing what already exists, not just resetting
      // the step pointer.
      apiFetch<{ cohorts: Array<{ id: string; name: string; semester: string }> }>(`/api/academic/tenants/${tenantId}/cohorts`),
    ])
      .then(async ([info, progress, cohortsResp]) => {
        if (cancelled) return;
        setRecap(info);
        setStep(Math.min(progress.currentStep, STEP_COUNT - 1));

        const existingCohort = cohortsResp.cohorts[0];
        if (existingCohort) {
          setCreatedCohortId(existingCohort.id);
          setCohortName(existingCohort.name);
          setCohortSemester(existingCohort.semester);
          try {
            const teamsResp = await apiFetch<{ teams: Array<{ id: string; name: string }> }>(
              `/api/academic/cohorts/${existingCohort.id}/teams`,
            );
            if (!cancelled) setCreatedTeams(teamsResp.teams);
          } catch {
            // Non-blocking — the cohort recap still shows; the team list
            // just starts empty and any teams typed get added fresh.
          }
        }
      })
      .catch(() => {
        // Fail open — start at step 0 with no recap data (recap section
        // just shows "unavailable" rather than blocking the wizard).
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId]);

  const advance = async (nextStep: number, completed = false) => {
    setSaving(true);
    try {
      await apiFetch('/api/onboarding/TENANT_ADMIN', {
        method: 'PATCH',
        body: JSON.stringify({ currentStep: nextStep, completed }),
      });
    } catch {
      // Non-blocking.
    } finally {
      setSaving(false);
    }
    if (completed) {
      navigate('/projects', { replace: true });
    } else {
      setStep(nextStep);
    }
  };

  const handleCreateTeams = async () => {
    if (!tenantId) return;
    setTeamError('');
    setSaving(true);
    try {
      let cohortId = createdCohortId;
      if (!cohortId) {
        const cohort = await apiFetch<{ id: string }>(`/api/academic/tenants/${tenantId}/cohorts`, {
          method: 'POST',
          body: JSON.stringify({ name: cohortName, semester: cohortSemester }),
        });
        cohortId = cohort.id;
        setCreatedCohortId(cohortId);
      }

      const names = teamNamesRaw.split('\n').map(n => n.trim()).filter(Boolean);
      const newTeams: Array<{ id: string; name: string }> = [];
      for (const name of names) {
        const team = await apiFetch<{ id: string; name: string }>(`/api/academic/cohorts/${cohortId}/teams`, {
          method: 'POST',
          body: JSON.stringify({ name }),
        });
        newTeams.push({ id: team.id, name: team.name });
      }
      // Append, don't replace — createdTeams may already hold teams loaded
      // from an existing cohort (mount-time check above); re-entering this
      // step and adding more must not drop those from view.
      setCreatedTeams(prev => [...prev, ...newTeams]);
      setTeamNamesRaw('');
      await advance(5);
    } catch (err) {
      setTeamError(err instanceof Error ? err.message : 'Failed to create teams');
    } finally {
      setSaving(false);
    }
  };

  const handleBatchInvite = async () => {
    if (!tenantId) return;
    setInviteError('');
    setSaving(true);
    try {
      const emails = inviteEmails.split('\n').map(e => e.trim()).filter(Boolean);
      const result = await apiFetch<{ summary: { sent: number; failed: number } }>(
        `/api/academic/tenants/${tenantId}/invite-batch`,
        { method: 'POST', body: JSON.stringify({ emails, teamAssignment: inviteTeamId || null }) },
      );
      setInviteResult(result.summary);
      await advance(6);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invitations');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={cardStyle}>
        <div className="flex items-center justify-center mb-6" style={{ gap: 8 }}>
          {Array.from({ length: STEP_COUNT }, (_, i) => (
            <div key={i} style={{ width: 24, height: 4, borderRadius: 2, background: i <= step ? 'var(--primary)' : 'var(--border)' }} />
          ))}
        </div>

        {step === 0 && (
          <RecapStep title="Institution" value={recap?.institutionName ?? 'Unavailable'} onNext={() => advance(1)} saving={saving} />
        )}
        {step === 1 && (
          <RecapStep
            title="Semester dates"
            value={recap ? `${recap.semesterStart} – ${recap.semesterEnd}` : 'Unavailable'}
            onNext={() => advance(2)}
            saving={saving}
          />
        )}
        {step === 2 && (
          <RecapStep title="Faculty supervisor" value={recap?.facultySupervisorEmail ?? 'Unavailable'} onNext={() => advance(3)} saving={saving} />
        )}
        {step === 3 && (
          <RecapStep title="API key" value={recap?.hasApiKey ? 'On file' : 'Not on file'} onNext={() => advance(4)} saving={saving} />
        )}

        {step === 4 && (
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>
              Team names configuration
            </h2>

            {createdCohortId ? (
              // R1/R2 follow-up fix — a cohort already exists (from a prior
              // pass through this step, detected on mount) — Q8's
              // pre-filled/editable restart semantics: show what's already
              // there instead of offering to create it again.
              <>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>Cohort</p>
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
                  {cohortName} ({cohortSemester})
                </p>
                {createdTeams.length > 0 && (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>Teams already created</p>
                    <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
                      {createdTeams.map(t => (
                        <li key={t.id} style={{ fontSize: 14, color: 'var(--text)' }}>{t.name}</li>
                      ))}
                    </ul>
                  </>
                )}
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Add more teams (optional, one per line)</label>
              </>
            ) : (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Cohort name</label>
                <input style={{ ...inputStyle, marginBottom: 16 }} value={cohortName} onChange={e => setCohortName(e.target.value)} placeholder="REC Fall 2026" />
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Semester</label>
                <input style={{ ...inputStyle, marginBottom: 16 }} value={cohortSemester} onChange={e => setCohortSemester(e.target.value)} placeholder="Fall 2026" />
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Team names (one per line)</label>
              </>
            )}
            <textarea
              style={{ ...inputStyle, minHeight: 100, marginBottom: 16 }}
              value={teamNamesRaw}
              onChange={e => setTeamNamesRaw(e.target.value)}
              placeholder={'Team Alpha\nTeam Beta'}
            />
            {teamError && <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 12 }}>{teamError}</p>}
            <button
              style={{ ...buttonStyle, width: '100%', opacity: saving || !cohortName || !cohortSemester ? 0.6 : 1 }}
              disabled={saving || !cohortName || !cohortSemester}
              onClick={handleCreateTeams}
            >
              {saving ? 'Saving…' : createdCohortId ? 'Continue' : 'Create cohort & teams'}
            </button>
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>
              Batch student invite
            </h2>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Student emails (one per line)</label>
            <textarea
              style={{ ...inputStyle, minHeight: 100, marginBottom: 16 }}
              value={inviteEmails}
              onChange={e => setInviteEmails(e.target.value)}
              placeholder={'alice@rec.edu\nbob@rec.edu'}
            />
            {createdTeams.length > 0 && (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>Team (optional)</label>
                <select style={{ ...inputStyle, marginBottom: 16 }} value={inviteTeamId} onChange={e => setInviteTeamId(e.target.value)}>
                  <option value="">Unassigned</option>
                  {createdTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </>
            )}
            {inviteError && <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 12 }}>{inviteError}</p>}
            <button
              style={{ ...buttonStyle, width: '100%', opacity: saving || !inviteEmails.trim() ? 0.6 : 1 }}
              disabled={saving || !inviteEmails.trim()}
              onClick={handleBatchInvite}
            >
              {saving ? 'Sending…' : 'Send invitations'}
            </button>
          </div>
        )}

        {step === 6 && (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              Setup complete
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
              {createdTeams.length > 0
                ? `${createdTeams.length} team(s) created${inviteResult ? `, ${inviteResult.sent} invitation(s) sent` : ''}.`
                : 'Your academic tenant is set up.'}
            </p>
            <button style={{ ...buttonStyle, width: '100%' }} onClick={() => advance(6, true)}>Go to Projects</button>
          </div>
        )}
      </div>
    </div>
  );
}

function RecapStep({ title, value, onNext, saving }: { title: string; value: string; onNext: () => void; saving: boolean }) {
  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{title}</h2>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>{value}</p>
      <button style={{ ...buttonStyle, width: '100%', opacity: saving ? 0.7 : 1 }} disabled={saving} onClick={onNext}>
        Next
      </button>
    </div>
  );
}
